import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import { Box, Grid, Typography, Paper, Stack, Chip, Select, MenuItem, FormControl, useTheme, Avatar, alpha } from "@mui/material";
import { 
  TrendingUpOutlined as RevenueIcon, 
  CurrencyRupeeOutlined as MoneyIcon, 
  PeopleOutlined as UserIcon, 
  LocalActivityOutlined as TicketIcon,
  BarChart as AnalyticsIcon,
  FilterList as FilterIcon
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { Line, Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler } from "chart.js";
import { getPartnerAnalytics } from "../../store/slices/analyticsSlice";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const PartnerAnalytics = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { user: clerkUser } = useUser();
  const { partnerAnalytics } = useSelector(s => s.analytics);
  const isDark = mode === 'dark';
  const [period, setPeriod] = useState('30d');

  // Standardized Palette
  const colors = useMemo(() => ({
    primary: "#666CFF",
    success: "#72E128",
    warning: "#FDB528",
    error: "#FF4D49",
    info: "#26C6F9",
    textPrimary: isDark ? "rgba(231, 227, 252, 0.87)" : "rgba(58, 53, 65, 0.87)",
    textSecondary: isDark ? "rgba(231, 227, 252, 0.6)" : "rgba(58, 53, 65, 0.6)",
    background: isDark ? "#282A42" : "#ffffff",
    divider: isDark ? "rgba(231, 227, 252, 0.12)" : "rgba(58, 53, 65, 0.12)",
  }), [isDark]);

  useEffect(() => {
    if (clerkUser?.id) {
      const theaterId = localStorage.getItem('activeTheaterId');
      dispatch(getPartnerAnalytics({ userId: clerkUser.id, period, theaterId }));
    }
  }, [dispatch, clerkUser?.id, period]);

  const data = partnerAnalytics;
  const o = data?.overview || {};

  const cardStyle = {
    bgcolor: colors.background,
    borderRadius: "12px",
    boxShadow: isDark ? "0 4px 18px 0 rgba(0,0,0, 0.4)" : "0 2px 10px 0 rgba(76, 78, 100, 0.08)",
    p: 3,
    height: "100%",
    border: `1px solid ${colors.divider}`,
    transition: 'all .25s ease-in-out',
  };

  const chartOpts = { 
    responsive: true, 
    maintainAspectRatio: false, 
    plugins: { 
      legend: { display: false },
      tooltip: {
        backgroundColor: colors.background,
        titleColor: colors.textPrimary,
        bodyColor: colors.textSecondary,
        borderColor: colors.divider,
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
      }
    }, 
    scales: { 
      x: { 
        grid: { display: false }, 
        ticks: { color: colors.textSecondary, font: { size: 10, weight: 600 } } 
      }, 
      y: { 
        grid: { color: colors.divider, drawTicks: false }, 
        ticks: { color: colors.textSecondary, font: { size: 10, weight: 600 } } 
      } 
    } 
  };

  const stats = [
    { label: 'GTV Revenue', val: `₹${(o.totalRevenue||0).toLocaleString()}`, icon: <MoneyIcon />, col: colors.primary },
    { label: 'Booking Load', val: (o.totalBookings||0).toLocaleString(), icon: <AnalyticsIcon />, col: colors.info },
    { label: 'Unit Sales', val: (o.totalTicketsSold||0).toLocaleString(), icon: <TicketIcon />, col: colors.success },
    { label: 'ACV Per Ticket', val: `₹${o.avgTicketPrice||0}`, icon: <RevenueIcon />, col: colors.warning },
    { label: 'System Utility', val: `${o.occupancyRate||0}%`, icon: <UserIcon />, col: '#8b5cf6' },
  ];

  return (
    <Box sx={{ pb: 5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.5 }}>
            Operational Insights
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
            Deep-dive into theatrical performance metrics and revenue velocity
          </Typography>
        </Box>
        <FormControl size="small" variant="filled">
          <Select 
            value={period} 
            onChange={e => setPeriod(e.target.value)} 
            sx={{ 
              borderRadius: '10px', 
              fontSize: '0.85rem', 
              fontWeight: 800, 
              bgcolor: alpha(colors.textPrimary, 0.03),
              "& .MuiSelect-select": { py: 1, px: 2 }
            }}
          >
            <MenuItem value="7d">Last 7 Cycles</MenuItem>
            <MenuItem value="30d">Standard 30D</MenuItem>
            <MenuItem value="90d">Quarterly Audit</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((s, i) => (
          <Grid item xs={6} sm={4} md key={i}>
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i*0.05 }}>
              <Paper elevation={0} sx={{ ...cardStyle, p: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(s.col, 0.1), color: s.col, borderRadius: '8px' }}>
                    {React.cloneElement(s.icon, { sx: { fontSize: 18 } })}
                  </Avatar>
                  <Typography sx={{ fontSize: '0.65rem', color: colors.textSecondary, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{s.label}</Typography>
                </Stack>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, color: colors.textPrimary }}>{s.val}</Typography>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={cardStyle}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: colors.textPrimary }}>Revenue Velocity Trend</Typography>
              <Chip label="REAL-TIME" size="small" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 900, bgcolor: alpha(colors.success, 0.12), color: colors.success }} />
            </Box>
            <Box sx={{ height: 320 }}>
              <Line 
                data={{ 
                  labels: data?.dailyData?.map(d => new Date(d.date).getDate()) || [], 
                  datasets: [{ 
                    label: 'Revenue', 
                    data: data?.dailyData?.map(d => d.revenue) || [], 
                    borderColor: colors.primary, 
                    backgroundColor: alpha(colors.primary, 0.12), 
                    fill: true, 
                    tension: 0.4, 
                    pointRadius: 4, 
                    pointHoverRadius: 6,
                    borderWidth: 3 
                  }] 
                }} 
                options={chartOpts} 
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={cardStyle}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: colors.textPrimary }}>Cyclical Load</Typography>
              <Chip label="BOOKINGS" size="small" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 900, bgcolor: alpha(colors.info, 0.12), color: colors.info }} />
            </Box>
            <Box sx={{ height: 320 }}>
              <Bar 
                data={{ 
                  labels: data?.dailyData?.map(d => new Date(d.date).getDate()) || [], 
                  datasets: [{ 
                    label: 'Bookings', 
                    data: data?.dailyData?.map(d => d.bookings) || [], 
                    backgroundColor: alpha(colors.info, 0.7), 
                    borderRadius: 4,
                    hoverBackgroundColor: colors.info 
                  }] 
                }} 
                options={chartOpts} 
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PartnerAnalytics;
